<template lang="pug">
.bounty-tab-container
  //- 头部资产模块（按版本切换）
  HitchProperty(
    v-if="isHitchCarFare"
    :points="points"
    :bounty="bounty"
    :bountyLoaded="bountyLoaded"
    :hitchBonusData="hitchBonusData"
    @click-receive="collectBonus"
  )
  BountyTabHeader(
    v-else
    :points="points"
    :bounty="bounty"
    :rewardCount="rewardCount"
    :isDueSoon="isDueSoon"
    :bountyLoaded="bountyLoaded"
    :signExtInfo="signInExtInfo"
    @awardClick="collectBonus"
    @signBubbleClick="showSignInPopup"
  )

  //- 模块列表
  .bounty-tab-modules
    template(v-for="m in modules")
      component.fuli-module(
        :is="FULI_CENTER_MODULE_COMPONENT[m.key]",
        :id="m.key"
        :class="[m.key, { 'exchange-list-style': m.key === 'welfare_bountyExchange' }]"
        :moduleKey="m.key"
        :moduleData="m.data"
        :moduleConfig="m.moduleConfig"
        :points="points"
        :bounty="bounty",
        :rakeBackModuleInfo="rakeBackModuleInfoMap[m.key] || {}"
        :signInModuleInfo="signInModuleInfo"
        :showTaskPopup="showTaskPopup"
        :rewardGuidingRules="rewardGuidingRules"
        @bounty="refreshBounty",
        @expired="handleModuleExpired(m.key)"
      )

    //- 金币领取动画&声音
    .collect-points-animate
      audio(id="audio" src="https://m.hellobike.com/resource/gallery/833/1_DdlUufFu.wav")
      Lottie.collect-points-lottie(
        :class="{ 'is-mix-page': isMixPage && isApp }"
        url="https://m.hellobike.com/resource/gallery/833/rCppTVu3_3d.json"
        :autoplay="false"
        :loop="false"
        ref="lottieComponent"
      )

    //- 首屏弹窗
    component(
      v-if="popupWindowVisible && popupComponent"
      :is="popupComponent"
      :visible="popupWindowVisible"
      :info="popupWindowData.data"
      @close="handlePopupWindowClose"
      @refresh="getPopupWindow"
    )

    //- 签到弹窗
    SignInPopup(
      :visible="signInPopupVisible"
      :signPlanList="signInPlanList"
      :extInfo="signInExtInfo"
      :showGuide="showSignGuide"
      :signInAdInfo="signInAdInfo"
      @close="handleSignInPopupClose"
      @sign="handleSignBtnClick"
    )
</template>

<script>
import { env } from '@hb/NBridge/lib/h5';
import eventBus from '@/const/eventBus';
import { getVersion } from '@/utils/systemCode';
import {
  safeJsonStringify,
  compareVersions,
  anchorToEl,
} from '@/utils/tools';
import { getTokenAndKey, getTokenAndLocation } from '@/utils';
import {
  getReceiveRewardCount,
  collectReward,
  getPopupWindow,
  checkin,
  queryTaskCanReceiveBonus,
} from '@/api/fuli';
import { getBountyBasicInfo } from '@/api/bounty';
import Toast from '@/components/toast';
import { ParamsUtil } from '@/utils/params';
import {
  uploadEvent,
  rakeBackBannerAdapter,
  getRakeBackBanner,
  loadAndShowSignPopup,
  getPageModuleList,
  getRewardGuideRules,
  anchorToModule,
} from '../common/helper';
import { getSignInData, judgeManualSignVersion } from '../common/sign';
import { setRebateModuleKey } from '../common/task';
import SPM from '../common/spm';
import { updateWidgetContent } from '../common/bridge';

import BountyTabHeader from './cpns/bounty-tab-header';
import RecommendedGoods from './cpns/recommended-goods';
import VideoRedPacket from './cpns/video-red-packet';
import TaskModule from './cpns/task-module';
import RakeBack from './cpns/rake-back';
import ExchangeModule from './cpns/exchange-module';
import TaskWrapper from './cpns/task-wrapper';
import SignInPopup from './cpns/sign-in-popup';
import NewcomerGuide from './cpns/newcomer-guide';
import CommonImagePopup from './cpns/common-image-popup';
import TreasureBoxFloat from './cpns/treasure-box';
import AdvMaster from '../components/adv-master';
import HitchProperty from './hitch-property';
import ExchangeFloat from './cpns/exchange-float';
import FirstScreenPopupScheduler, {
  parseBackendPopup,
  isPopupAnchorKey,
} from './first-screen-popup';

import {
  FULI_MODULE,
  FULI_CENTER_MODULE_COMPONENT,
  POPUP_WINDOW_KEY,
  WELFARE_CENTER_POPUP_WINDOW_COMPONENT,
  WIDGET_TEMPLATE_ID,
  WIDGET_SOURCE,
} from '../consts';

export default {
  name: 'BountyTab',
  components: {
    VideoRedPacket,
    TaskModule,
    RakeBack,
    ExchangeModule,
    BountyTabHeader,
    RecommendedGoods,
    TaskWrapper,
    SignInPopup,
    NewcomerGuide,
    TreasureBoxFloat,
    CommonImagePopup,
    AdvMaster,
    HitchProperty,
    ExchangeFloat,
    Lottie: () => import('@/components/lottie'),
  },
  props: {
    // 页面版本
    styleVersion: {
      type: String,
      default: '',
    },
    // 是否为聚合页面
    isMixPage: {
      type: Boolean,
      default: false,
    },
    // 是否为福利中心顺风车心智版本
    isHitchCarFare: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // 用户是否登录
      isLogin: true,
      // 用户token
      token: '',
      // 模块列表
      modules: [],
      // 模块组件枚举
      FULI_CENTER_MODULE_COMPONENT,
      // 首屏弹窗组件枚举
      WELFARE_CENTER_POPUP_WINDOW_COMPONENT,
      // 奖励金可兑金额
      bounty: 0,
      // 奖励金数量
      points: 0,
      // 奖励金加载状态
      bountyLoaded: false,
      // 顺风车心智版本：HitchProperty 接口数据透传（queryTaskCanReceiveBonus.data 原样）
      hitchBonusData: {},
      // 电商返佣模块信息（按 moduleKey 存储）
      rakeBackModuleInfoMap: {},
      // 单单返币奖励金数量
      rewardCount: 0,
      // 单单返奖励金是否即将过期
      isDueSoon: false,
      // 是否展示单单返入口
      isShowDDFEntry: false,
      // 首屏弹窗数据
      popupWindowData: {
        key: POPUP_WINDOW_KEY.checkin_window,
        styleType: 0,
        data: {},
      },
      // 首屏弹窗是否展示
      popupWindowVisible: false,
      // 是否支持广告
      isSupportAd: true,
      // 是否是App端
      isApp: env.isEasyBikeApp,
      // 签到弹窗控制
      signInPopupVisible: false,
      // 签到广告信息
      signInAdInfo: null,
      // 签到计划列表
      signInPlanList: [],
      // 签到扩展信息
      signInExtInfo: {},
      // 签到模块信息
      signInModuleInfo: null,
      // 是否触发展示任务弹窗
      showTaskPopup: false,
      // 奖励金引导规则信息配置
      rewardGuidingRules: [],
      // 公告数据
      noticeData: {},
      // 用户ID
      userNewId: '',
    };
  },
  provide() {
    return {
      popupWindowInfo: () => this.popupWindowData,
      isSupportAd: () => this.isSupportAd,
      currentlyIsVip: () => (this.signInExtInfo && this.signInExtInfo.isVip),
      isShowDDFEntry: () => this.isShowDDFEntry,
      // 福利中心实验版本（用于埋点 experimentalGroup 参数）
      styleVersion: this.styleVersion,
    };
  },
  inject: ['titleBarHeight'],
  computed: {
    // 是否展示签到引导图片（后端 hasShowTips=true开关 且用户未签到）
    showSignGuide() {
      const { didSignToday, extraConfig = {} } = this.signInExtInfo || {};
      return !!extraConfig.hasShowTips && !didSignToday;
    },
    // 当前首屏弹窗组件（根据 styleType 决定）
    popupComponent() {
      const { styleType, key } = this.popupWindowData;
      if (styleType) return 'CommonImagePopup';
      return WELFARE_CENTER_POPUP_WINDOW_COMPONENT[key] || null;
    },
  },
  async created() {
    // 登录状态初始化
    const { isLogin } = await this.initUser();
    if (!isLogin) return;

    // 首屏弹窗调度器：统一管理签到弹窗 / 后端首屏弹窗 / 任务弹窗的展示与优先级
    this.popupScheduler = new FirstScreenPopupScheduler({
      getUserId: () => this.userNewId,
      getAnchorModule: () => this.$route.query.anchorModule,
      getBackendPopup: () => this.popupWindowData,
      openVuePopup: () => { this.popupWindowVisible = true; },
      showSignPopup: () => this.showSignInPopup(),
      openTaskPopup: () => { this.showTaskPopup = true; },
    });

    // 初始化页面数据
    this.initPageData();
    this.checkRefreshComponentContent();
    // 页面进入曝光
    this.handlePageView();
    // 页面事件上传
    uploadEvent();
    // 监听页面可见性变化
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  mounted() {
    // 金币领取动画全局注册
    eventBus.$on('playCollectAnimate', () => {
      this.playCollectAnimate();
    });
    // 新人引导签到任务触发签到弹窗
    eventBus.$on('showSignInPopup', () => {
      this.showSignInPopup();
    });
  },
  destroyed() {
    // 移除页面可见性变化监听
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    // 移除金币领取动画监听
    eventBus.$off('playCollectAnimate');
    // 移除签到弹窗监听
    eventBus.$off('showSignInPopup');
    // 页面退出埋点
    SPM.pageViewOut({
      businessInfo: {
        abtest: this.styleVersion,
      },
    });
  },
  methods: {
    // 初始化用户信息
    async initUser() {
      const result = await getTokenAndKey();
      const { token, userNewId } = result || {};
      this.isLogin = !!token;
      this.token = token;
      this.userNewId = userNewId || '';

      return { token, isLogin: !!token };
    },
    // 初始化页面数据
    async initPageData() {
      // 判断页面是否支持广告 & 获取弹窗信息
      await Promise.all([
        this.judgePageAdSupport(),
        this.getPopupWindow(),
      ]);
      await this.getPageModuleInfo();
      // 获取奖励金信息
      this.refreshBounty();
      // 获取单单返信息
      this.getReceiveReward();
      // 顺风车心智版本：拉取 HitchProperty 可领取奖励金数据
      this.fetchHitchData();
      // 签到数据
      this.initSignData();
    },
    // 获取单单返信息
    async getReceiveReward() {
      this.isShowDDFEntry = true;
      try {
        const result = await getReceiveRewardCount({ token: this.token });
        const { data } = result || {};
        const {
          totalRewardValue = 0, // 单单返待领取奖励金总和
          isDueSoon = false, // 待领取单单返是否即将到期
          rewardGuidingRulesList = [],
          sceneNameEns,
        } = data || {};

        // 单单返奖励金信息
        this.rewardCount = +totalRewardValue || 0;
        this.isDueSoon = isDueSoon;
        // 奖励金领取场景
        this.rewardSceneNameEns = sceneNameEns;
        // 新人兑换引导规则
        this.rewardGuidingRules = getRewardGuideRules(rewardGuidingRulesList);

        if (+totalRewardValue) {
          SPM.moduleExpose({
            pageId: 'welfare_new',
            categoryId: 'platform',
            moduleId: 'platform_dandanfanlingqu',
            contentId: 'platform_dandanfanlingqu',
            exposureTimes: 1,
            businessInfo: {},
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('getReceiveReward => error', safeJsonStringify(err));
      }
    },
    // 获取/刷新奖励金
    async refreshBounty() {
      try {
        const result = await getBountyBasicInfo({
          token: this.token,
          pointType: 1,
        });
        const { amount = 0, points = 0 } = result.data || {};
        this.bounty = amount;
        this.points = points;
        this.bountyLoaded = true;
        // 奖励金数量变化事件
        eventBus.$emit('pointsChange', { points });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('refreshBounty => error', safeJsonStringify(error));
      }
    },
    // 获取弹窗信息
    async getPopupWindow() {
      const { adsource = '' } = this.$route.query;
      try {
        const result = await getPopupWindow({
          token: this.token,
          adsource,
          welfareNewVersion: true,
        });

        const { data } = result || {};
        const popup = parseBackendPopup(data);
        if (popup) {
          this.popupWindowData = popup;
          // 交由调度器决定立即展示还是让位给链接签到弹窗
          this.popupScheduler.onBackendPopupReady();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('getPopupWindow => error', safeJsonStringify(error));
      }
    },
    // 页面可见性变化
    async handleVisibilityChange() {
      if (document.visibilityState === 'visible' && this.isLogin) {
        this.refreshBounty();
        this.getReceiveReward();
        // 顺风车心智版本：刷新资产模块（HitchProperty）的可领取数据
        this.fetchHitchData();
        // 先等模块信息更新完（内部会 setRebateModuleKey），再通知任务列表刷新
        await this.getPageModuleInfo({ isRefresh: true });
        eventBus.$emit('refreshTaskList');

        // 检查是否有锚定标记（从明细页返回时触发）
        const needAnchor = localStorage.getItem('welfare_need_anchor');
        if (needAnchor) {
          localStorage.removeItem('welfare_need_anchor');
          this.$nextTick(() => {
            setTimeout(() => {
              eventBus.$emit('earnPointsAnchor');
            }, 500);
          });
        }
      }
    },
    // 顺风车心智版本：拉取/刷新 HitchProperty 可领取奖励金数据
    async fetchHitchData() {
      if (!this.isHitchCarFare) return;
      try {
        const result = await queryTaskCanReceiveBonus({ token: this.token });
        this.hitchBonusData = (result && result.data) || {};
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('fetchHitchData => error', safeJsonStringify(error));
        this.hitchBonusData = {};
      }
    },
    // 领取单单返奖励金
    async collectBonus() {
      try {
        SPM.clickButton({
          pageId: 'welfare_new',
          buttonName: 'platform_dandanfanlingqu',
          categoryId: 'platform',
          businessInfo: {},
        });
        const { adCode, cityCode } = await getTokenAndLocation();

        let sceneNameEns = ['platform_points'];
        if (this.rewardSceneNameEns && this.rewardSceneNameEns.length > 0) {
          sceneNameEns = this.rewardSceneNameEns;
        }

        const params = {
          token: this.token,
          sceneCode: 'D001',
          condition: {
            sceneNameEns,
            statusSet: [1],
          },
          adCode,
          cityCode,
        };
        const { code } = await collectReward(params);
        if (code !== 0) return;
        // 更新单单返状态
        this.getReceiveReward();
        // 顺风车心智版本：刷新资产模块（HitchProperty）的可领取数据
        this.fetchHitchData();
        this.playCollectAnimate();
        // 展示单单返引导弹窗
        eventBus.$emit('showAwardGuidePopup');
      } catch (error) {
        Toast.show('领取失败');
        // eslint-disable-next-line no-console
        console.error('collectBonus => error', safeJsonStringify(error));
      }
    },
    // 领取奖励动画
    playCollectAnimate() {
      const audio = document.getElementById('audio');
      audio.src = 'https://m.hellobike.com/resource/gallery/833/1_DdlUufFu.wav';
      audio.play().catch(() => { });
      const lottieInstance = this.$refs.lottieComponent.instance;
      if (lottieInstance) {
        lottieInstance.goToAndStop(0, true); // 从头开始
        lottieInstance.play();
      }
      // 延时更新奖励金信息
      setTimeout(() => {
        this.refreshBounty();
        audio.src = '';
      }, 1200);
    },
    // 处理页面曝光
    handlePageView() {
      // 页面曝光埋点
      const params = ParamsUtil.getParams();
      SPM.pageView({
        businessInfo: {
          abtest: this.styleVersion,
          ...params,
        },
      });
      // 奖励金信息模块曝光埋点
      SPM.moduleExpose({
        moduleId: 'my_bonus_new',
        contentId: 'my_bonus_new',
        businessInfo: {},
      });
    },
    // 获取页面模块
    async getPageModuleInfo(options) {
      const { isRefresh = false } = options || {};
      try {
        const { cityCode } = await getTokenAndLocation();
        const { moduleList } = await getPageModuleList({
          cityCode,
          token: this.token,
          pageType: 26,
        });
        if (moduleList.length === 0) return;

        // 处理页面模块配置
        this.handleModuleList({ moduleList });

        this.$nextTick(() => {
          if (!isRefresh) {
            setTimeout(() => {
              this.scrollAnchor();
            }, 500);
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('getPageModuleInfo => error', safeJsonStringify(error));
      }
    },
    // 处理页面模块配置
    async handleModuleList(options) {
      const { moduleList } = options || {};
      this.modules = moduleList
        .map((m) => {
          const { moduleKey, moduleData = [] } = m || {};
          const { data = {} } = moduleData[0] || {};

          const {
            moduleMainTitle, moduleSubtitle, moduleSubtitleLogo, moduleSubtitleLogoSwitch,
          } = moduleData[0] || {};
          // 模块通用配置（标题/副标题/图标等可配置项，后续可扩展背景图等）
          const moduleConfig = {
            mainTitle: moduleMainTitle || '',
            subtitle: moduleSubtitle || '',
            subtitleLogo: moduleSubtitleLogo || '',
            subtitleLogoSwitch: !!moduleSubtitleLogoSwitch,
          };

          // 提取公告数据并过滤掉该模块
          if (moduleKey === FULI_MODULE.welfare_notice) {
            const { noticeBoard, jumpUrl } = moduleData[0] || {};
            this.noticeData = { noticeBoard, jumpUrl };
            this.$emit('notice-data', this.noticeData);
            return false;
          }

          // welfare_banner 模块：直接使用 moduleData[0] 作为 data
          if (moduleKey === FULI_MODULE.welfare_banner) {
            return { key: moduleKey, data: moduleData[0] || {}, moduleConfig };
          }

          // 处理电商返佣额外信息
          if (m.moduleKey === FULI_MODULE.welfare_rake_back
          || m.moduleKey === FULI_MODULE.welfare_takeout_rebate_module
          || m.moduleKey === FULI_MODULE.welfare_cheese_rebate_module) {
            if (!this.isApp) return false;
            setRebateModuleKey(moduleKey);

            const rakeBackData = (m.moduleKey === FULI_MODULE.welfare_rake_back)
              ? data
              : (moduleData[0] || {});
            this.handleRakeBackExtInfo(moduleKey, rakeBackData);
          }
          if (
            m.moduleKey === FULI_MODULE.welfare_advertisement_video
            && !this.isSupportAd
          ) {
            return false;
          }
          return { key: moduleKey, data, moduleConfig };
        })
        .filter(Boolean);
    },
    // 处理页面滚动锚定
    scrollAnchor() {
      const { anchorModule, hasSweepLight } = this.$route.query;
      if (!anchorModule) return;

      // 锚定 key 命中首屏弹窗 => 走弹窗直达，跳过 DOM 滚动
      // 签到弹窗依赖签到数据，由 initSignData 就绪后在调度器中触发
      if (isPopupAnchorKey(anchorModule)) {
        return;
      }

      setTimeout(() => {
        if (hasSweepLight === '1') {
          // 定位 + 扫光动效
          this.anchorTimer = anchorToModule(
            { anchoringModuleKey: anchorModule },
            this.anchorTimer,
          );
        } else {
          // 仅定位
          const titleBarHeight = this.titleBarHeight();
          anchorToEl(anchorModule, titleBarHeight + 10);
        }
      }, 500);
    },
    // 处理电商返佣额外信息
    async handleRakeBackExtInfo(moduleKey, rakebackData) {
      if (
        [
          FULI_MODULE.welfare_takeout_rebate_module,
          FULI_MODULE.welfare_cheese_rebate_module].includes(moduleKey)
      ) {
        // banner直接返回了不需要额外接口处理
        this.$set(this.rakeBackModuleInfoMap, moduleKey, {
          ...rakebackData,
          moduleKey,
          styleVersion: this.styleVersion,
        });
        return;
      }

      // 获取电商返佣banner信息
      const { success, result } = await getRakeBackBanner();
      if (!success) return;

      // 处理电商返佣banner信息
      this.$set(this.rakeBackModuleInfoMap, moduleKey, {
        ...rakeBackBannerAdapter(result),
        moduleKey,
        styleVersion: this.styleVersion,
      });
    },
    // 判断页面广告支持情况
    async judgePageAdSupport() {
      // 非App端 => 不支持广告
      if (!this.isApp) {
        this.isSupportAd = false;
        return;
      }

      // App端 => 版本必须要满足6.88.5及以上
      try {
        const version = await getVersion();
        const isSupport = compareVersions(version, '6.88.5') !== -1;
        this.isSupportAd = isSupport;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('judgePageAdSupport => error', safeJsonStringify(error));
      }
    },
    // 签到数据初始化
    async initSignData() {
      // 获取签到信息
      const { success, data } = await getSignInData({
        token: this.token,
      });
      if (success) {
        // 触发金币更新
        this.refreshBounty();
        const {
          signInAdInfo,
          signInPlanList,
          signInExtInfo,
          signInModuleInfo,
        } = data || {};

        this.signInAdInfo = signInAdInfo;
        this.signInPlanList = signInPlanList;
        this.signInExtInfo = signInExtInfo;
        this.signInModuleInfo = signInModuleInfo;
        // 签到数据就绪，交由调度器决定是否弹出签到弹窗
        this.popupScheduler.onSignDataReady({
          hasSignData: (this.signInPlanList || []).length > 0,
          didSignToday: !!(signInExtInfo && signInExtInfo.didSignToday),
        });
      }
    },
    // 签到弹窗链全部关闭后的后续弹窗调度（补弹后端首屏弹窗 / 任务弹窗）
    handleAllPopupClosed() {
      this.popupScheduler.onAllSignPopupClosed();
    },
    // 展示签到弹窗
    async showSignInPopup() {
      const { adsource = '' } = this.$route.query;
      if (this.isApp) {
        // 检测app是否支持手动签到（版本 >= 6.99.10）
        const isSupport = await judgeManualSignVersion();
        if (!isSupport) {
          // 不支持 => 走H5签到兜底
          this.signInPopupVisible = true;
          return;
        }

        // 支持 => 拉起app签到弹窗
        await loadAndShowSignPopup({
          ubtData: {
            abtest: this.styleVersion,
            adsource,
          },
          signInExtInfo: this.signInExtInfo,
          signInAdInfo: this.signInAdInfo,
          onSign: () => this.handleSignBtnClick(),
          onAdClear: () => { this.signInAdInfo = null; },
          onAllPopupClosed: () => this.handleAllPopupClosed(),
        });
      } else {
        // 非App端 => 拉起H5签到弹窗
        this.signInPopupVisible = true;
      }
    },
    // 用户手动签到
    async handleSignBtnClick() {
      try {
        await checkin({
          token: this.token,
          newWelfare: true,
        });
        this.initSignData();
        this.refreshBounty();
        // 刷新新人引导任务状态
        eventBus.$emit('refreshNewcomerGuide');
        eventBus.$emit('refreshTaskList');
        // 签到成功后，通知刷新小组件
        updateWidgetContent({ templateId: WIDGET_TEMPLATE_ID, source: WIDGET_SOURCE });
      } catch (error) {
        Toast.show('签到失败请重试');
      }
    },
    // 关闭签到弹窗
    handleSignInPopupClose() {
      this.signInPopupVisible = false;
      // H5签到弹窗关闭后，尝试展示任务弹窗
      this.handleAllPopupClosed();
    },
    // 链接参数refreshComponentContent=true时，调用刷新小组件内容桥方法
    checkRefreshComponentContent() {
      const { refreshComponentContent } = this.$route.query;
      if (refreshComponentContent === 'true') {
        updateWidgetContent({ templateId: WIDGET_TEMPLATE_ID, source: WIDGET_SOURCE });
      }
    },

    // 关闭首屏弹窗
    handlePopupWindowClose() {
      this.popupWindowVisible = false;
    },
    // 模块过期移除
    handleModuleExpired(key) {
      this.modules = this.modules.filter((m) => m.key !== key);
      // 新人引导模块兑换完成，重新拉取模块列表
      if (key === FULI_MODULE.welfare_newcomer_guide) {
        this.getPageModuleInfo({ isRefresh: true });
      }
    },
  },
};
</script>

<style lang="less" scoped>
.bounty-tab-container {
  .bounty-tab-modules {
    margin-top: 0.06rem;
  }

  .fuli-module {
    margin: 0 0.12rem 0.08rem;
  }

  .exchange-list-style {
    margin: 0;
    width: 100%;
  }

  .collect-points-lottie {
    position: fixed;
    z-index: 100;
    top: calc(0.5rem + var(--welfare-notice-height, 0));
    left: 0;
    width: 100%;
    pointer-events: none;

    &.is-mix-page {
      top: calc(0.95rem + var(--welfare-notice-height, 0));
    }
  }
}

/* 全局锚定高亮样式 */
/deep/ .shine-light-animation {
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    z-index: 9;
    border: 0.02rem solid #fa4614;
    border-radius: 0.12rem;
    animation: blink-border 1.5s infinite;
  }

  &::after {
    content: '';
    z-index: 9;
    position: absolute;
    display: inline-block;
    left: 0;
    top: 0;
    width: 1rem;
    height: 100%;
    opacity: 0.9;
    transform: skewX(150deg);
    background: linear-gradient(90deg, transparent, #fff 30%, transparent);
    animation: move-light 1.1s infinite;
  }

  @keyframes move-light {
    100% {
      left: 125%;
    }
  }

  @keyframes blink-border {
    0%,
    100% {
      border-color: #fa4614;
    }

    50% {
      border-color: rgba(250, 70, 20, 30%);
    }
  }
}
</style>
